import React, { useCallback, useEffect, useRef } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Login from "../views/Login/Login";
// import Organizations from "../views/Organizations/Organizations";
import { useDispatch, useSelector } from "react-redux";
import { updateLoggedIn } from "../redux/authStore";
import axiosInstance from "../utils/utils";
import HomePage from "../views/Home/HomePage";
import { setUserData } from "../redux/userStore";
import QualityManagementSystem from "../views/QualityManagementSystem/QualityManagementSystem";
import {
  resetConfluenceStore,
  setConfluenceTree,
} from "../redux/confluenceStore";
import ResourcesArchive from "../views/ResourcesArchive/ResourcesArchive";
import KnowledgeBase from "../views/KnowledgeBase/KnowledgeBase";
import ManagementReports from "../views/ManagementReports/ManagementReports";
import { showNotFoundPage } from "../redux/layoutStore";
import PageLoader from "../components/Loader/PageLoader";
import { setLoadingContext } from "../redux/loaderStore";
import Audits from "../views/Audits/Audits";
import AuditDetails from "../views/AuditDetails/AuditDetails";
import Findings from "../views/Findings/Findings";
import NewFinding from "../views/NewFinding/NewFinding";
import RiskAnalysis from "../views/RiskAnalysis/RiskAnalysis";
import RiskControlCenter from "../views/RiskControlCenter/RiskControlCenter";
import ImprovementProjects from "../views/ImprovementProjects/ImprovementProjects";
import ImprovementProjectDetails from "../views/ImprovementProjectDetails/ImprovementProjectDetails";
import FindingDetails from "../views/FindingDetails/FindingDetails";
import { getOrganizationData } from "../actions/organization";
import {
  CONFLUENCE_TREE_LOADER,
  PAGE_LOADER,
} from "../constants/loader";
import { showModal } from "../redux/modalStore";
import ManageAudits from "../views/ManageAudits/ManageAudits";
import ManageUsersAndOrganizations from "../views/ManageUsers&Organizations/ManageUsersAndOrganizations";
import { useMediaQuery } from "@mui/material";
import CopyAudits from "../views/Copy Audits/CopyAudits";
import RiskProfile from "../views/RiskProfile/RiskProfile";
import Archived from "../views/Archived/Archived";
import CustomsData from "../views/CustomsData/CustomsData";
import {
  clearLoginUiState,
  clearOrgContext,
  clearSessionToken,
  mirrorOrgId,
  setSessionToken,
} from "../utils/auth";
import Welcome from "../views/Welcome/Welcome";
import NoOrganizations from "../views/NoOrganizations/NoOrganizations";

const Router = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const location = useLocation();
  // const orgId = localStorage.getItem("orgId");
  const isMobile = useMediaQuery("(max-width:768px)");

  const orgId = location.pathname.split("/")[1];
  const hasNavigatedRef = useRef(false);
  const hasValidatedRef = useRef(false);

  const loggedIn = useSelector((state) => state.auth.loggedIn);
  const module = useSelector((state) => state.user.modules[orgId]);

  const fetchingTree = useRef(false);
  const fetchingPage = useRef(false);

  const getUserData = (sessionToken, refresh = false) => {
    const refreshQuery = refresh ? "&refresh=true" : "";
    const options = {
      method: "GET",
      url: `/auth/session?session_token=${sessionToken}${refreshQuery}`,
    };

    return axiosInstance
      .request(options)
      .then((response) => {
        dispatch(setUserData(response.data));
        return response.data;
      })
      .catch((error) => {
        if (error.response && error.response.status === 401) {
          // Handle invalid session token (401 Unauthorized)
          clearSessionToken();
          clearOrgContext();
          clearLoginUiState();
          dispatch(updateLoggedIn(false));
          navigate("/login", { replace: true, state: { expired: true } });
          error.handled = true;
        } else {
          console.error("Error validating token:", error);
          dispatch(
            setLoadingContext({ context: PAGE_LOADER, isLoading: false }),
          );
          dispatch(showModal({ name: "error_modal" }));
        }
        throw error;
      });
  };

  const selectFirstOrganizationId = useCallback((orgsSource) => {
    return (
      Object.values(orgsSource || {})
        .map((org) => ({
          id: org?.UUID || org?.id,
          name: org?.Name || org?.name || "",
        }))
        .filter((org) => org.id)
        .sort((a, b) => a.name.localeCompare(b.name))[0]?.id || null
    );
  }, []);

  // const setSessionToken = (token) => {
  //   // Store in localStorage (old behaviour)
  //   localStorage.setItem("session_token", token);
  //
  //   // store as a cookie for subdomain sharing
  //   const isLocal = window.location.hostname === "localhost";
  //   const cookieDomain = isLocal ? "" : "domain=.border.ly;";
  //
  //   document.cookie = `session_token=${token}; ${cookieDomain} path=/; secure; samesite=None`;
  // };
  //
  // const clearSessionToken = () => {
  //   localStorage.removeItem("session_token");
  //   const isLocal = window.location.hostname === "localhost";
  //   const cookieDomain = isLocal ? "" : "domain=.border.ly;";
  //   // Expire cookie
  //   document.cookie = `session_token=; ${cookieDomain} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
  // };
  //
  // const clearLoginUiState = () => {
  //   localStorage.removeItem("login_email");
  //   localStorage.removeItem("login_email_sent");
  //   localStorage.removeItem("login_sent_at");
  // };

  // AUTHENTICATION
  useEffect(() => {
    const validateAndFetch = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");
      const sessionToken = localStorage.getItem("session_token");

      if (hasValidatedRef.current && !token) return;

      dispatch(updateLoggedIn("pending"));

      const handleLogout = () => {
        clearSessionToken(); // LS + cookie
        clearOrgContext(); // org_id LS + cookie
        clearLoginUiState(); // resend screen flags
        dispatch(updateLoggedIn(false));
        navigate("/login", { replace: true });
      };

      const handleNoSession = () => {
        clearSessionToken(); // LS + cookie
        clearOrgContext(); // org_id LS + cookie
        clearLoginUiState(); // resend screen flags
        dispatch(updateLoggedIn(false));

        if (location.pathname === "/login") {
          navigate("/login", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      };

      const bootstrapOrganizations = async (userData, activeSessionToken) => {
        const orgs = userData?.data?.organizations;
        const orgIdFromPath = location.pathname.split("/")[1];
        const hasOrgAccess = orgs && orgs[orgIdFromPath];

        const targetOrgId = hasOrgAccess
          ? orgIdFromPath
          : selectFirstOrganizationId(orgs);

        if (!targetOrgId) {
          dispatch(updateLoggedIn(true));
          navigate("/no-organizations", { replace: true });
          return;
        }

        await dispatch(
          getOrganizationData(
            targetOrgId,
            activeSessionToken,
            navigate,
            !hasOrgAccess,
          ),
        );
        dispatch(updateLoggedIn(true));
      };

      try {
        if (token) {
          const response = await axiosInstance.request({
            method: "PATCH",
            url: "/auth/session",
            params: { auth_token: token },
          });

          if (response.status === 200) {
            const newSessionToken = response.data.session_token;
            setSessionToken(newSessionToken);
            const userData = await getUserData(newSessionToken, true);
            await bootstrapOrganizations(userData, newSessionToken);
          } else {
            console.error("Unexpected status code:", response.status);
            handleLogout();
          }
        } else if (sessionToken) {
          const userData = await getUserData(sessionToken, true);
          await bootstrapOrganizations(userData, sessionToken);
        } else {
          handleNoSession();
        }
      } catch (error) {
        if (error?.handled) {
          return;
        }
        if (error.response && error.response.status === 401) {
          handleLogout();
        } else {
          console.error("Error validating token:", error);
          dispatch(
            setLoadingContext({ context: PAGE_LOADER, isLoading: false }),
          );
          dispatch(showModal({ name: "error_modal" }));
        }
      } finally {
        hasValidatedRef.current = true;
      }
    };

    validateAndFetch();
  }, [dispatch, location.pathname, navigate, selectFirstOrganizationId]);

  useEffect(() => {
    if (!orgId || orgId === "no-organizations") return;
    // mirror orgId to cookie and localStorage so insights can read it
    mirrorOrgId(orgId);
  }, [orgId]);

  // CONFLUENCE DOCUMENT TREE
  const getConfluenceTree = useCallback(
    async (path, sessionToken) => {
      // Use a ref or variable to prevent duplicate calls
      if (fetchingTree.current) return;
      fetchingTree.current = true;

      dispatch(
        setLoadingContext({ context: CONFLUENCE_TREE_LOADER, isLoading: true }),
      );

      // Find the matching item in the data array
      const item = module?.find((data) => `/${orgId}${data.path}` === path);

      if (!item || item.module !== "ConfluenceDocumentTree" || !item.params) {
        dispatch(
          setLoadingContext({
            context: CONFLUENCE_TREE_LOADER,
            isLoading: false,
          }),
        );
        fetchingTree.current = false; // Reset fetching flag
        return;
      }

      const { space, page_name, page_id } = item.params;
      let queryParams = `org_id=${orgId}`;

      if (space) {
        queryParams += `&space_key=${space}`;
      }

      if (page_name) {
        queryParams += `&page_name=${encodeURIComponent(page_name)}`;
      } else if (page_id) {
        queryParams += `&page_id=${page_id}`;
      }

      const url = `/confluence/tree?${queryParams}`;

      try {
        const response = await axiosInstance.get(url, {
          headers: { "session-token": sessionToken },
        });
        dispatch(showNotFoundPage(false));
        dispatch(setConfluenceTree(response.data));
      } catch (error) {
        if (error.response && error.response.status === 404) {
          dispatch(showNotFoundPage(true));
        } else {
          dispatch(
            showModal({
              name: "error_modal",
            }),
          );
        }
      } finally {
        dispatch(
          setLoadingContext({
            context: CONFLUENCE_TREE_LOADER,
            isLoading: false,
          }),
        );
        fetchingTree.current = false; // Reset fetching flag
      }
    },
    [dispatch, module, orgId],
  );

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  };

  const sessionToken =
    localStorage.getItem("session_token") || getCookie("session_token");

  useEffect(() => {
    // Ensure the user is logged in and module data is available before calling getConfluenceTree
    if (loggedIn === true && module) {
      const currentPath = location.pathname;
      getConfluenceTree(currentPath, sessionToken);
    }
  }, [location.pathname, loggedIn, module, sessionToken, getConfluenceTree]);

  //useRef to prevent loops
  useEffect(() => {
    if (loggedIn === true && orgId && !hasNavigatedRef.current) {
      // If user is on the root path or login page, navigate to organization path
      if (location.pathname === "/" || location.pathname === "/login") {
        dispatch(resetConfluenceStore());
        navigate(`/${orgId}`, { replace: true });
        hasNavigatedRef.current = true;
      } else {
        hasNavigatedRef.current = true;
      }
    }
  }, [loggedIn, orgId, dispatch, navigate, location.pathname]);

  const DefaultOrgRedirect = () => {
    const orgs = useSelector((state) => state.user.organizations);
    const orgValues = Object.values(orgs || {});
    const orgIdFromStorage = localStorage.getItem("org_id");
    const allowedIds = new Set(
      orgValues
        .map((org) => org?.UUID || org?.id)
        .filter(Boolean),
    );
    const canUseStored = orgIdFromStorage && allowedIds.has(orgIdFromStorage);
    const fallbackOrgId = canUseStored
      ? orgIdFromStorage
      : selectFirstOrganizationId(orgs);

    if (fallbackOrgId) {
      return <Navigate to={`/${fallbackOrgId}`} replace />;
    }

    return <Navigate to="/no-organizations" replace />;
  };

  return (
    <Routes>
      {/* {loggedIn === true && (
        <Route path="*" element={<Navigate to={"/organizations"} replace />} />
      )}
      {loggedIn === true && (
        <Route index path="/organizations" element={<Organizations />} />
      )} */}
      {loggedIn === true && <Route path="/:orgId" element={<HomePage />} />}
      {loggedIn === true && (
        <Route path="/no-organizations" element={<NoOrganizations />} />
      )}
      {loggedIn === true && (
        <Route path="/organizations" element={<DefaultOrgRedirect />} />
      )}
      {loggedIn === true && (
        <Route path="*" element={<DefaultOrgRedirect />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route
          path="/:orgId/qms/:pageId?"
          element={
            <QualityManagementSystem getConfluenceTree={getConfluenceTree} />
          }
        />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/resources" element={<ResourcesArchive />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/knowledge-base" element={<KnowledgeBase />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route
          path="/:orgId/management-reports"
          element={<ManagementReports />}
        />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/audits" element={<Audits />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/audits/:auditKey" element={<AuditDetails />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route
          path="/:orgId/improvement-projects"
          element={<ImprovementProjects />}
        />
      )}
      {loggedIn === true && !isMobile && (
        <Route
          path="/:orgId/improvement-projects/:improvementKey"
          element={<ImprovementProjectDetails />}
        />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/findings" element={<Findings />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/findings/:findingKey" element={<FindingDetails />} />
      )}
      {loggedIn === true && isMobile && (
        <Route path="/:orgId/new-finding" element={<NewFinding />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/risk-analysis" element={<RiskAnalysis />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route
          path="/:orgId/risk-control-center"
          element={<RiskControlCenter />}
        />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/risk-profile" element={<RiskProfile />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/manage-audits" element={<ManageAudits />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/admin" element={<ManageUsersAndOrganizations />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/copy-audits" element={<CopyAudits />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/archived" element={<Archived />} />
      )}
      {loggedIn === true && !isMobile && (
        <Route path="/:orgId/customs-data" element={<CustomsData />} />
      )}
      {loggedIn === false && <Route path="/" element={<Welcome />} />}
      {loggedIn === false && <Route path="/login" element={<Login />} />}
      {loggedIn === false && (
        <Route path="*" element={<Navigate to={"/"} replace />} />
      )}
      {loggedIn === "pending" && (
        <Route
          path="*"
          element={
            <PageLoader translationKey="pageLoader.preparingAccount" />
          }
        />
      )}
    </Routes>
  );
};

export default Router;
